/**
 * Saved plays (#92) — the plays a coach names and keeps, apart from the board.
 *
 * The board is a working surface; a saved play is a copy of it the coach was
 * happy with. Both are play documents, so a saved play is stored, versioned
 * and upgraded exactly as the board is (`serialization.ts`): its `id` is the
 * play's id, its `name` the play's name.
 *
 * The board remembers which saved play it came from in `playId`. That link is
 * part of the board's history on purpose: undoing the opening of a play
 * brings back the board that was there, and with it the play that board was
 * linked to — so a later Save can never write one play's content into another.
 */

import type { Id, IdFactory } from './ids.ts';
import type { PlayDocument } from './document.ts';

/** As long as a play's name may be. The server refuses longer ones. */
export const MAX_PLAY_NAME = 60;

/** What a board is called before it was ever saved as a play. Never a saved play's name. */
export const UNTITLED_PLAY = 'Untitled play';

/** The key two names are compared by: what a coach sees as the same name is the same key. */
export function playNameKey(name: string): string {
  return name.trim().toLowerCase();
}

/** The saved play the board holds right now, ready to store under `id` and `name`. */
export function toSavedPlay(board: PlayDocument, id: Id, name: string, now: number, createdAt = now): PlayDocument {
  const { playId: _link, ...play } = board;
  return { ...play, id, name: name.trim(), createdAt, updatedAt: now };
}

/** The board a saved play opens as: the play itself, linked to it. */
export function openPlay(play: PlayDocument): PlayDocument {
  return { ...play, playId: play.id };
}

/** The board, linked to a play it was just saved as. */
export function linkToPlay(board: PlayDocument, play: PlayDocument): PlayDocument {
  return { ...board, playId: play.id, name: play.name };
}

/** The board, no longer linked to any saved play. */
export function unlink(board: PlayDocument): PlayDocument {
  if (board.playId === undefined) return board;
  const { playId: _link, ...rest } = board;
  return { ...rest, name: UNTITLED_PLAY };
}

/**
 * New play: the same teams — who is in the water, their numbers, position
 * names and where they stand in the first phase — with the play itself
 * started over: one phase, no drawings, no saved play behind it.
 */
export function newPlay(board: PlayDocument, newId: IdFactory, now: number): PlayDocument {
  const first = board.keyframes[0];
  if (!first) throw new Error('Document has no keyframes');
  return {
    ...unlink(board),
    id: newId('play'),
    name: UNTITLED_PLAY,
    createdAt: now,
    updatedAt: now,
    shapes: [],
    keyframes: [{ id: newId('kf'), positions: { ...first.positions } }],
  };
}

/** JSON with object keys sorted, so two equal values always read the same. */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, inner: unknown) =>
    inner && typeof inner === 'object' && !Array.isArray(inner)
      ? Object.fromEntries(Object.entries(inner as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      : inner,
  );
}

/**
 * Whether the board still holds exactly the play as it was saved: the same
 * pieces, drawings and phases. The view is only a camera, and the name, ids
 * and times are bookkeeping, so none of them count as a change.
 */
export function matchesPlay(board: PlayDocument, play: PlayDocument): boolean {
  const content = (doc: PlayDocument) => canonical({ entities: doc.entities, shapes: doc.shapes, keyframes: doc.keyframes });
  return content(board) === content(play);
}
